import { Checkbox, DateInput } from '@orbind/ui'
import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import { api } from '../../lib/api.ts'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useWiMutation, useWorkItemDetail, useWorkItemList, type WorkItemDetail } from './api.ts'
import { Dropdown, SectionHeader, StatusPill, initialsOf, useConfirm } from './bits.tsx'
import { ChildRowControls } from './ChildRowControls.tsx'
import { jaEstaNoFim, ordemAoSoltar, ordemNoFim } from './checklist-ordem.ts'
import { linhasColadas } from './linhas-coladas.ts'
import { ListaDePessoas } from './ListaDePessoas.tsx'
import { ConverterItemModal } from './ConverterItemModal.tsx'
import { ConcluirEmCadeiaModal, type NoDaCascata } from './ConcluirEmCadeiaModal.tsx'
import { ReabrirEmCadeiaModal } from './ReabrirEmCadeiaModal.tsx'
import { ExcluirComFilhosModal } from './ExcluirComFilhosModal.tsx'
import { ExcluirChecklistModal } from './ExcluirChecklistModal.tsx'
import { SeletorDeItens } from './SeletorDeItens.tsx'
import { useT } from '../../lib/i18n.tsx'

/**
 * Checklists de um item DESCENDENTE, abertos dentro da linha que o cita.
 *
 * É a própria seção que se repete: as linhas de baixo têm exatamente as mesmas
 * ações das de cima (etapa, status, membros, data, editar, remover), e a
 * hierarquia continua descendo sozinha, sem código paralelo para manter.
 */
function SubChecklists({
  itemId,
  bootstrap,
  onOpenItem,
}: {
  itemId: string
  bootstrap: Bootstrap
  onOpenItem: (id: string) => void
}) {
  const detalhe = useWorkItemDetail(itemId)
  if (detalhe.isPending) return <p className="wi-cl__netoload">Carregando…</p>
  if (!detalhe.data) return null
  return (
    <ChecklistsSection
      detail={detalhe.data}
      bootstrap={bootstrap}
      onOpenItem={onOpenItem}
      // "Filhos" já aparece no nível de cima; aqui interessa a cadeia de listas
      mostrarFilhos={false}
    />
  )
}

/**
 * Secao Filhos/Checklists do detalhe (fatia 04 do prototipo):
 * varios checklists nomeados, itens internos ou vinculados, drafts inline,
 * filhos sem checklist ("orfaos"), modal de nome e modal de busca.
 */

/** Data curta para o title do botão; vazio vira travessão. */
const fmtData = (v: string | null) =>
  v ? new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' }).format(new Date(v)) : '—'

type Checklist = WorkItemDetail['collab']['checklists'][number]
type ChecklistItem = Checklist['items'][number]

export function ChecklistsSection({
  detail,
  bootstrap,
  onOpenItem,
  createNonce = 0,
  draftChildNonce = 0,
  filhosOpen = true,
  onToggleFilhos,
  onAddChild,
  expandirSinal = 0,
  expandirValor = true,
  mostrarChecklists = true,
  mostrarFilhos = true,
  infoFilhos,
}: {
  detail: WorkItemDetail
  bootstrap: Bootstrap
  onOpenItem: (id: string) => void
  /** Incrementado pelo menu "Adicionar" para abrir o modal de novo checklist. */
  createNonce?: number
  /** Incrementado pelo menu "Adicionar" para abrir o draft de novo filho. */
  draftChildNonce?: number
  /** Estado recolhido do tópico "Filhos" (controlado pelo detalhe). */
  filhosOpen?: boolean
  onToggleFilhos?: () => void
  onAddChild?: () => void
  /** "Expandir/Recolher tudo" do detalhe: nonce + valor propagam aos checklists. */
  expandirSinal?: number
  expandirValor?: boolean
  /**
   * Liga/desliga de cada metade da seção, vindo do menu "..." (2026-08-30):
   * checklists e filhos são coisas diferentes e se escondem separado.
   */
  mostrarChecklists?: boolean
  /** O "i" de instrucoes da secao Filhos (2026-09-01). */
  infoFilhos?: ReactNode
  mostrarFilhos?: boolean
}) {
  const t = useT()
  const workItemId = detail.item.id
  const confirm = useConfirm()

  const [draftFor, setDraftFor] = useState<string | 'orphans' | null>(null)
  /** Checklists recolhidos — cada um abre e fecha por conta propria. */
  const [recolhidos, setRecolhidos] = useState<Set<string>>(new Set())
  /** Timer que segura o clique simples ate saber se virou duplo clique. */
  const cliqueRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => { if (cliqueRef.current) clearTimeout(cliqueRef.current) }, [])
  /** Item cujo titulo esta em edicao inline (dois cliques). */
  const [editandoTitulo, setEditandoTitulo] = useState<{ id: string; texto: string; linkedId: string | null } | null>(null)
  const [draftText, setDraftText] = useState('')
  /** 'create-filho' cria o checklist NO ITEM FILHO da linha (rowId abre a linha). */
  /** Item cuja conclusão em cadeia está sendo confirmada. */
  const [concluindoEmCadeia, setConcluindoEmCadeia] = useState<{ id: string; titulo: string } | null>(null)
  /**
   * Item cuja reabertura em cadeia está sendo perguntada, com a prévia do que
   * volta junto (`nos === null`: a prévia falhou). SPEC-20261006-1355-desmarcar-em-cadeia.
   */
  const [reabrindoEmCadeia, setReabrindoEmCadeia] = useState<
    { id: string; titulo: string; nos: NoDaCascata[] | null } | null
  >(null)
  const [nameModal, setNameModal] = useState<
    { mode: 'create' } | { mode: 'rename'; checklist: Checklist } | { mode: 'create-filho'; filhoId: string; rowId: string } | null
  >(null)
  const [nameValue, setNameValue] = useState('')
  /** Filho cuja remocao esta sendo decidida: so o vinculo ou o item inteiro. */
  /**
   * Item de Trabalho que se quer remover — venha da seção de filhos ou de uma
   * LINHA de checklist vinculada (SPEC-20261006-1304). `linhaId` existe no
   * segundo caso: "desfazer o vínculo" ali é tirar a linha do checklist, e
   * excluir o item leva a linha junto, para ela não ficar apontando para um
   * item que saiu das listas.
   */
  const [removendoFilho, setRemovendoFilho] = useState<{
    id: string
    code: string
    title: string
    linhaId?: string
  } | null>(null)
  /** Exclusão esperando resposta sobre a descendência. */
  const [excluindoComFilhos, setExcluindoComFilhos] = useState<{
    id: string
    code: string
    title: string
    linhaId?: string
    filhos: number
  } | null>(null)
  // "Selecionar item existente": o seletor único, com filtro
  // (SPEC-20261008-2157-filtro-no-seletor-de-itens)
  const [pickerOpen, setPickerOpen] = useState(false)
  const abrirPicker = () => setPickerOpen(true)
  const fecharPicker = () => setPickerOpen(false)

  useEffect(() => {
    if (createNonce > 0) {
      setNameModal({ mode: 'create' })
      setNameValue('')
    }
  }, [createNonce])

  /** Confirma o modal de nome, qualquer que seja o modo. */
  const confirmarNome = () => {
    const nome = nameValue.trim()
    if (!nome || !nameModal) return
    if (nameModal.mode === 'create') createChecklist.mutate(nome)
    else if (nameModal.mode === 'rename') renameChecklist.mutate({ checklistId: nameModal.checklist.id, name: nome })
    else {
      const { filhoId, rowId } = nameModal
      criarChecklistNoFilho.mutate(
        { filhoId, name: nome },
        {
          onSuccess: () => {
            // o detalhe e a subarvore do NIVEL de baixo tem cache proprio
            void qcLocal.invalidateQueries({ queryKey: ['work-item', filhoId] })
            void qcLocal.invalidateQueries({ queryKey: ['work-item', 'schedule'] })
            setAbertas((atual) => new Set(atual).add(rowId))
          },
        },
      )
    }
    setNameModal(null)
  }

  useEffect(() => {
    if (draftChildNonce > 0) {
      setDraftFor('orphans')
      setDraftText('')
    }
  }, [draftChildNonce])

  // "Expandir/Recolher tudo" do detalhe recolhe/expande também os checklists
  useEffect(() => {
    if (expandirSinal === 0) return
    setRecolhidos(expandirValor ? new Set() : new Set(detail.collab.checklists.map((c) => c.id)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandirSinal])

  const createChecklist = useWiMutation(workItemId, async (name: string) =>
    api['work-items'][':id'].checklists.$post({ param: { id: workItemId }, json: { name } }),
  )
  /**
   * Checklist criado direto num FILHO, sem abrir o filho (pedido 2026-09-09).
   * Depois de criar, a linha do filho abre sozinha mostrando o checklist novo
   * para os itens ja serem digitados ali — e o gesto se repete em cada nivel.
   */
  const qcLocal = useQueryClient()
  const criarChecklistNoFilho = useWiMutation(workItemId, async (input: { filhoId: string; name: string }) =>
    api['work-items'][':id'].checklists.$post({ param: { id: input.filhoId }, json: { name: input.name } }),
  )
  const renameChecklist = useWiMutation(workItemId, async (input: { checklistId: string; name: string }) =>
    api['work-items'].checklists[':checklistId'].$patch({
      param: { checklistId: input.checklistId },
      json: { name: input.name },
    }),
  )
  /**
   * `itens` diz o que acontece com os Itens de Trabalho a que as linhas
   * apontam (SPEC-20261006-1304-excluir-item-com-filhos): ficam (o de sempre),
   * saem, ou saem com a descendência.
   */
  const removeChecklist = useWiMutation(
    workItemId,
    async (input: { checklistId: string; itens?: 'manter' | 'excluir' | 'excluir-com-filhos' }) =>
      api['work-items'].checklists[':checklistId'].$delete({
        param: { checklistId: input.checklistId },
        query: { itens: input.itens ?? 'manter' },
      }),
  )

  /** Checklist esperando resposta sobre os itens vinculados. */
  const [excluindoChecklist, setExcluindoChecklist] = useState<{
    id: string
    nome: string
    itens: { id: string; code: string; title: string }[]
    filhos: number
  } | null>(null)

  /**
   * Apagar um checklist: com linhas vinculadas a itens, pergunta o que fazer
   * com eles; só com texto, a confirmação de sempre — com o texto certo.
   */
  const pedirExclusaoDoChecklist = async (checklist: { id: string; name: string }) => {
    try {
      const r = await api['work-items'].checklists[':checklistId'].vinculados.$get({
        param: { checklistId: checklist.id },
      })
      const dados = await r.json()
      if (dados.itens.length > 0) {
        setExcluindoChecklist({ id: checklist.id, nome: checklist.name, itens: dados.itens, filhos: dados.filhos })
        return
      }
      confirm.ask(
        dados.linhas === 0
          ? `O checklist "${checklist.name}" será removido.`
          : `O checklist "${checklist.name}" e ${dados.linhas === 1 ? 'a atividade dele' : `as ${dados.linhas} atividades dele`} serão removidos.`,
        () => removeChecklist.mutate({ checklistId: checklist.id }),
      )
    } catch {
      // sem saber o que há dentro, não se apaga às cegas: a pessoa tenta de novo
      confirm.ask(
        `Não foi possível conferir o que há no checklist "${checklist.name}". Remover só o checklist? Os itens vinculados continuam existindo.`,
        () => removeChecklist.mutate({ checklistId: checklist.id }),
      )
    }
  }
  const addItem = useWiMutation(workItemId, async (input: { checklistId: string; text?: string; linkedWorkItemId?: string }) =>
    api['work-items'].checklists[':checklistId'].items.$post({
      param: { checklistId: input.checklistId },
      json: { text: input.text ?? '', linkedWorkItemId: input.linkedWorkItemId ?? null },
    }),
  )
  const patchItem = useWiMutation(workItemId, async (input: { itemId: string; isDone: boolean }) =>
    api['work-items']['checklist-items'][':itemId'].$patch({
      param: { itemId: input.itemId },
      json: { isDone: input.isDone },
    }),
  )
  /**
   * Reordenar as linhas do checklist arrastando (pedido 2026-08-30).
   *
   * Como na lista de campos, o arraste vive numa ref e o destaque é escrito no
   * DOM: redesenhar no meio de um arraste HTML5 faz o navegador reiniciá-lo.
   * A ordem vai numa chamada só, com a lista inteira do checklist.
   */
  const arrasto = useRef<{ id: string; checklistId: string | null; alvo: string | null; antes: boolean } | null>(null)
  const caixaRef = useRef<HTMLDivElement>(null)

  const marcarDestino = (linha: HTMLElement | null, antes: boolean) => {
    for (const el of caixaRef.current?.querySelectorAll('.wi-cl__row') ?? []) {
      el.classList.remove('wi-cl__row--antes', 'wi-cl__row--depois')
    }
    linha?.classList.add(antes ? 'wi-cl__row--antes' : 'wi-cl__row--depois')
  }
  const limparArrasto = () => {
    marcarDestino(null, true)
    for (const el of caixaRef.current?.querySelectorAll('.wi-cl__row') ?? []) {
      el.classList.remove('wi-cl__row--indo')
    }
    arrasto.current = null
  }

  /**
   * Datas direto no checklist (pedido 2026-08-30). Linha que virou Item de
   * Trabalho grava no ITEM; linha comum grava nela mesma — o mesmo par de datas
   * que alimenta o cronograma.
   */
  const [datasDe, setDatasDe] = useState<string | null>(null)
  /** Prazo combinado do PRÓPRIO checklist — o mesmo que o cronograma usa. */
  const datasDoChecklist = useWiMutation(
    workItemId,
    async (input: { checklistId: string; startAt: string | null; dueAt: string | null }) =>
      api['work-items'].checklists[':checklistId'].$patch({
        param: { checklistId: input.checklistId },
        json: { startAt: input.startAt, dueAt: input.dueAt } as never,
      }),
  )

  /** Botão de data do CHECKLIST (cabeçalho), igual ao das linhas. */
  const botaoDeDataDoChecklist = (checklist: Checklist) => {
    const cl = checklist as unknown as { id: string; startAt: string | null; dueAt: string | null }
    const chave = `cl:${cl.id}`
    const gravar = (datas: { startAt: string | null; dueAt: string | null }) =>
      datasDoChecklist.mutate({ checklistId: cl.id, ...datas })
    return (
      <span className="wi-cl__datawrap">
        <button
          type="button"
          className={`wi-cl__iconbtn${cl.startAt || cl.dueAt ? ' wi-cl__data--on' : ''}`}
          aria-label="Datas do checklist"
          title={
            cl.startAt || cl.dueAt
              ? `${fmtData(cl.startAt)} → ${fmtData(cl.dueAt)}`
              : 'Prazo do checklist (as atividades podem esticar)'
          }
          onClick={() => setDatasDe((c) => (c === chave ? null : chave))}
        >
          <i className="far fa-calendar" aria-hidden style={{ fontSize: 10 }} />
        </button>
        {datasDe === chave ? (
          <span className="wi-cl__datapop">
            <label className="wi-cl__datacampo">
              <span>Início</span>
              <DateInput
                value={cl.startAt ? new Date(cl.startAt) : null}
                onChange={(d) => gravar({ startAt: d ? d.toISOString() : null, dueAt: cl.dueAt })}
              />
            </label>
            <label className="wi-cl__datacampo">
              <span>Prazo</span>
              <DateInput
                value={cl.dueAt ? new Date(cl.dueAt) : null}
                onChange={(d) => gravar({ startAt: cl.startAt, dueAt: d ? d.toISOString() : null })}
              />
            </label>
            <button type="button" className="wi-cl__datafechar" onClick={() => setDatasDe(null)}>
              Fechar
            </button>
          </span>
        ) : null}
      </span>
    )
  }
  const datasDaLinha = useWiMutation(
    workItemId,
    async (input: { itemId: string; startAt: string | null; dueAt: string | null }) =>
      api['work-items']['checklist-items'][':itemId'].$patch({
        param: { itemId: input.itemId },
        json: { startAt: input.startAt, dueAt: input.dueAt } as never,
      }),
  )
  const datasDoItem = useWiMutation(
    workItemId,
    async (input: { itemId: string; startAt: string | null; dueAt: string | null }) =>
      api['work-items'][':id'].$patch({
        param: { id: input.itemId },
        query: {},
        json: { startAt: input.startAt, dueAt: input.dueAt } as never,
      }),
  )

  /**
   * SUBÁRVORE do item (2026-08-30): serve só para saber QUEM tem checklist
   * pendurado, e portanto quem ganha a setinha de abrir. O conteúdo de cada
   * nível vem do detalhe do próprio item, para as ações não divergirem.
   */
  const subarvore = useQuery({
    queryKey: ['work-item', 'schedule', workItemId],
    queryFn: async () => (await api['work-items'][':id'].schedule.$get({ param: { id: workItemId } })).json(),
  })
  /** Linhas de checklist abertas (mostrando o que está pendurado nelas). */
  const [abertas, setAbertas] = useState<ReadonlySet<string>>(new Set())
  const alternarAberta = (id: string) =>
    setAbertas((atual) => {
      const proximo = new Set(atual)
      if (proximo.has(id)) proximo.delete(id)
      else proximo.add(id)
      return proximo
    })

  const listasPorItem = new Map<string, { id: string; name: string }[]>()
  for (const cl of subarvore.data?.checklists ?? []) {
    listasPorItem.set(cl.workItemId, [...(listasPorItem.get(cl.workItemId) ?? []), cl])
  }
  /** O item da linha tem algo pendurado? Só então a setinha aparece. */
  const temNetos = (id: string | undefined | null) => Boolean(id && (listasPorItem.get(id)?.length ?? 0) > 0)

  /**
   * Quem executa uma LINHA que não virou Item de Trabalho (2026-08-30).
   * Mesmo gesto do filho: avatares + "+" que liga/desliga cada membro.
   */
  const toggleMembroDaLinha = useWiMutation(
    workItemId,
    async (input: { itemId: string; memberId: string }) =>
      api['work-items']['checklist-items'][':itemId'].members[':memberId'].toggle.$post({
        param: { itemId: input.itemId, memberId: input.memberId },
      }),
  )
  const [membrosDe, setMembrosDe] = useState<string | null>(null)

  /**
   * RESPONSÁVEL pela linha (pedido 2026-09-21).
   *
   * A linha já dizia quem ACOMPANHA; agora diz quem RESPONDE. É um só, vem da
   * mesma lista pela estrela, e o servidor tira essa pessoa dos membros — os
   * dois papéis não se acumulam na mesma pessoa.
   */
  const responsavelDaLinha = useWiMutation(
    workItemId,
    async (input: { itemId: string; memberId: string | null }) =>
      api['work-items']['checklist-items'][':itemId'].$patch({
        param: { itemId: input.itemId },
        json: { assigneeMemberId: input.memberId } as never,
      }),
  )

  const botaoDeMembros = (item: ChecklistItem) => {
    const atuais = (item as { members?: { id: string; name: string }[] }).members ?? []
    const responsavelId = (item as { assigneeMemberId?: string | null }).assigneeMemberId ?? null
    const responsavel = responsavelId ? bootstrap.members.find((m) => m.id === responsavelId) : undefined
    return (
      <span className="wi-menuwrap wi-cl__membros">
        {responsavel ? (
          <span
            className="wi-chip-member wi-chip-member--responsavel"
            style={{ width: 24, height: 24, fontSize: 9 }}
            title={`${responsavel.name} — responsável pela linha`}
          >
            {initialsOf(responsavel.name)}
          </span>
        ) : null}
        {atuais.map((m) => (
          <span
            key={m.id}
            className="wi-chip-member"
            style={{ width: 24, height: 24, fontSize: 9 }}
            title={m.name}
          >
            {initialsOf(m.name)}
          </span>
        ))}
        <button
          type="button"
          className="wi-cl__resp"
          title="Quem executa esta linha"
          aria-label="Membros da linha"
          onClick={() => setMembrosDe((c) => (c === item.id ? null : item.id))}
        >
          <span className="wi-cl__resp-vazio" aria-hidden>
            <i className="fas fa-user-plus" style={{ fontSize: 10 }} />
          </span>
        </button>
        <Dropdown open={membrosDe === item.id} onClose={() => setMembrosDe(null)} align="left" width={240}>
          <ListaDePessoas
            pessoas={bootstrap.members}
            ehMembro={(id) => atuais.some((x) => x.id === id)}
            responsavelId={responsavelId}
            onAlternarMembro={(id) => toggleMembroDaLinha.mutate({ itemId: item.id, memberId: id })}
            onDefinirResponsavel={(id) => responsavelDaLinha.mutate({ itemId: item.id, memberId: id })}
            onFechar={() => setMembrosDe(null)}
          />
        </Dropdown>
      </span>
    )
  }

  /** Botão de data + editor de uma linha, igual nos dois tipos de linha. */
  const botaoDeData = (item: ChecklistItem) => {
    const ini = item.linked?.startAt ?? item.startAt ?? null
    const fim = item.linked?.dueAt ?? item.dueAt ?? null
    const gravar = (datas: { startAt: string | null; dueAt: string | null }) =>
      item.linked
        ? datasDoItem.mutate({ itemId: item.linked.id, ...datas })
        : datasDaLinha.mutate({ itemId: item.id, ...datas })
    return (
      <span className="wi-cl__datawrap">
        <button
          type="button"
          className={`wi-cl__data${ini || fim ? ' wi-cl__data--on' : ''}`}
          aria-label="Definir datas"
          title={ini || fim ? `${fmtData(ini)} → ${fmtData(fim)}` : 'Definir início e prazo'}
          onClick={() => setDatasDe((c) => (c === item.id ? null : item.id))}
        >
          <i className="far fa-calendar" aria-hidden />
        </button>
        {datasDe === item.id ? (
          <span className="wi-cl__datapop">
            <label className="wi-cl__datacampo">
              <span>Início</span>
              <DateInput
                value={ini ? new Date(ini) : null}
                onChange={(d) => gravar({ startAt: d ? d.toISOString() : null, dueAt: fim })}
              />
            </label>
            <label className="wi-cl__datacampo">
              <span>Prazo</span>
              <DateInput
                value={fim ? new Date(fim) : null}
                onChange={(d) => gravar({ startAt: ini, dueAt: d ? d.toISOString() : null })}
              />
            </label>
            <button type="button" className="wi-cl__datafechar" onClick={() => setDatasDe(null)}>
              Fechar
            </button>
          </span>
        ) : null}
      </span>
    )
  }

  const salvarOrdem = useWiMutation(workItemId, async (input: { checklistId: string; itemIds: string[] }) =>
    api['work-items'].checklists[':checklistId'].order.$patch({
      param: { checklistId: input.checklistId },
      json: { itemIds: input.itemIds },
    }),
  )

  /**
   * Mover a linha para OUTRO checklist, na posição onde foi solta
   * (SPEC-20260923-2331). A troca e a ordem vão juntas no servidor: em duas
   * chamadas, uma falha no meio deixaria a linha com a ordem do checklist
   * antigo — ela apareceria num lugar imprevisível.
   */
  const moverEntreChecklists = useWiMutation(
    workItemId,
    async (input: { itemId: string; checklistId: string; itemIds: string[] }) =>
      api['work-items']['checklist-items'][':itemId'].mover.$patch({
        param: { itemId: input.itemId },
        json: { checklistId: input.checklistId, itemIds: input.itemIds },
      }),
  )

  const soltarLinha = (alvo: ChecklistItem, checklistId: string | null) => {
    const a = arrasto.current
    limparArrasto()
    if (!a || !checklistId || a.id === alvo.id) return
    const destino = detail.collab.checklists.find((c) => c.id === checklistId)
    if (!destino) return
    /*
     * A linha tem de ser de um checklist DESTE item: a seção se repete dentro
     * das linhas dos descendentes, e um arraste que viesse de outra seria uma
     * troca de dono sem a pessoa perceber — o servidor recusa, e aqui nem
     * chega a sair.
     */
    const daSecao = detail.collab.checklists.some((c) => c.items.some((i) => i.id === a.id))
    if (!daSecao) return

    // a linha entra onde o traço apareceu: antes ou depois do alvo
    const itemIds = ordemAoSoltar(destino.items, a.id, alvo.id, a.antes)

    if (a.checklistId === checklistId) salvarOrdem.mutate({ checklistId, itemIds })
    else moverEntreChecklists.mutate({ itemId: a.id, checklistId, itemIds })
  }

  /** Solta no vazio da lista: a linha vai para o FIM daquele checklist. */
  const soltarNoFim = (checklistId: string) => {
    const a = arrasto.current
    limparArrasto()
    if (!a) return
    const destino = detail.collab.checklists.find((c) => c.id === checklistId)
    if (!destino) return
    const itemIds = ordemNoFim(destino.items, a.id)
    // já estava no fim desta lista: nada mudou, não vale uma ida ao servidor
    if (a.checklistId === checklistId && jaEstaNoFim(destino.items, a.id)) return
    if (a.checklistId === checklistId) salvarOrdem.mutate({ checklistId, itemIds })
    else moverEntreChecklists.mutate({ itemId: a.id, checklistId, itemIds })
  }

  const renameItem = useWiMutation(workItemId, async (input: { itemId: string; text: string }) =>
    api['work-items']['checklist-items'][':itemId'].$patch({ param: { itemId: input.itemId }, json: { text: input.text } }),
  )
  const renameLinked = useWiMutation(workItemId, async (input: { id: string; title: string }) =>
    api['work-items'][':id'].$patch({ param: { id: input.id }, query: {}, json: { title: input.title } }),
  )
  const deleteItem = useWiMutation(workItemId, async (itemId: string) =>
    api['work-items']['checklist-items'][':itemId'].$delete({ param: { itemId } }),
  )
  const convertItem = useWiMutation(
    workItemId,
    async (input: {
      itemId: string
      workItemTypeId: string
      workItemStatusId: string
      workItemPriorityId: string
      companyAreaId: string | null
      flowStepId: string | null
      description: string
    }) => {
      const { itemId, ...json } = input
      return api['work-items']['checklist-items'][':itemId'].convert.$post({ param: { itemId }, json })
    },
  )
  /** Linha que abriu o diálogo de conversão (pedido 2026-08-30). */
  const [convertendo, setConvertendo] = useState<{ id: string; texto: string } | null>(null)
  const toggleLinkedStatus = useWiMutation(workItemId, async (input: { linkedId: string; done: boolean }) => {
    // marcar concluido num item vinculado = transicionar o status do proprio WI
    const bootstrap = await (await api.catalog.bootstrap.$get()).json()
    /**
     * Alvo por PROPRIEDADE, não por código (2026-08-30): agora que os status
     * são todos cadastráveis, alguém pode excluir ou renomear o "DONE" — e
     * antes isso quebrava o checklist com "status alvo nao encontrado". Um
     * status terminal ativo é o que caracteriza "concluído", venha com o nome
     * que vier; o código só entra como último desempate.
     */
    const ativos = bootstrap.workItemStatuses.filter((s) => s.isActive)
    const target = input.done
      ? (ativos.find((s) => s.isTerminal && s.code === 'DONE') ?? ativos.find((s) => s.isTerminal))
      : (ativos.find((s) => s.isInitial) ?? ativos.find((s) => !s.isTerminal))
    if (!target) throw new Error('Nenhum status ativo serve para marcar como concluído. Revise o cadastro de Status.')
    return api['work-items'][':id'].status.$post({
      param: { id: input.linkedId },
      json: { statusId: target.id, ...(input.done ? {} : { comment: 'Reaberto pelo checklist do item pai' }) },
    })
  })
  const archiveChild = useWiMutation(
    workItemId,
    async (input: { id: string; comFilhos: boolean; linhaId?: string }) => {
      const r = await api['work-items'][':id'].archive.$post({
        param: { id: input.id },
        json: { comFilhos: input.comFilhos },
      })
      // a linha que apontava para o item sai junto: deixá-la seria mostrar no
      // checklist um item que já não está em lista nenhuma
      if (input.linhaId) {
        await api['work-items']['checklist-items'][':itemId'].$delete({ param: { itemId: input.linhaId } })
      }
      return r
    },
  )

  /**
   * Excluir um Item de Trabalho a partir do checklist: com descendência,
   * pergunta se ela vai junto; sem, a confirmação de sempre.
   */
  const pedirExclusao = async (alvo: { id: string; code: string; title: string; linhaId?: string }) => {
    let abaixo = 0
    try {
      const r = await api['work-items'][':id'].descendentes.$get({ param: { id: alvo.id } })
      abaixo = (await r.json()).total
    } catch {
      // sem o número, pergunta-se o de sempre — não saber não trava a exclusão
    }
    if (abaixo > 0) {
      setExcluindoComFilhos({ ...alvo, filhos: abaixo })
      return
    }
    confirm.ask(`${alvo.code} — ${alvo.title} será excluído e sai das listas e quadros.`, () =>
      archiveChild.mutate({ id: alvo.id, comFilhos: false, linhaId: alvo.linhaId }),
    )
  }
  /**
   * Desmarcar um item vinculado (SPEC-20261006-1355-desmarcar-em-cadeia): com
   * coisas concluídas embaixo, pergunta se elas voltam junto; sem, reabre
   * direto — uma janela com um caminho só seria um clique a mais sem escolha.
   *
   * A prévia é buscada AQUI, e não pela janela, porque é ela que diz se há o
   * que perguntar. Se ela falhar, a janela abre mesmo assim, com só "Reabrir
   * só este item": não saber o que está embaixo não pode virar reabrir tudo
   * calado, nem deixar o item travado.
   */
  const pedirReabertura = async (alvo: { id: string; title: string }) => {
    let nos: NoDaCascata[] | null = null
    try {
      const r = await api['work-items'][':id'].reabertura.$get({ param: { id: alvo.id } })
      nos = (await r.json()).nos
    } catch {
      // segue com `nos` nulo: a janela mostra o erro e oferece só o item
    }
    if (nos !== null && nos.length <= 1) {
      toggleLinkedStatus.mutate({ linkedId: alvo.id, done: false })
      return
    }
    setReabrindoEmCadeia({ id: alvo.id, titulo: alvo.title, nos })
  }
  const unlinkChild = useWiMutation(workItemId, async (childId: string) =>
    api['work-items'][':id'].move.$post({ param: { id: childId }, json: { parentId: null } }),
  )
  // vincular um Item de Trabalho JA EXISTENTE como filho: reparenta o item
  // escolhido para debaixo deste (o backend recusa ciclo/autorreferencia)
  const linkChild = useWiMutation(workItemId, async (childId: string) =>
    api['work-items'][':id'].move.$post({ param: { id: childId }, json: { parentId: workItemId } }),
  )

  const ancestrais = detail.breadcrumb.flatMap((b) => (b ? [b.id] : []))
  // ao vincular filho existente, esconde da busca o proprio item, os ancestrais
  // (evita ciclo) e os que ja sao filhos (evita duplicar)
  const idsInvalidosComoFilho = new Set<string>([
    workItemId,
    ...ancestrais,
    ...detail.children.all.map((child) => child.id),
  ])
  // no checklist, some da busca o que JÁ está num checklist do item — o
  // vínculo duplicava (pedido do Cristian, 2026-10-08)
  const idsJaNoChecklist = new Set<string>([
    workItemId,
    ...ancestrais,
    ...detail.collab.checklists.flatMap((c) => c.items.flatMap((i) => (i.linked ? [i.linked.id] : []))),
  ])
  /**
   * Inclui os marcados em sequência, na ordem marcada (pedido do Cristian,
   * 2026-10-08: "poder marcar mais de um item para incluir por vez"); para no
   * primeiro erro e devolve o que faltou — a janela fica com eles.
   */
  const incluirMarcados = async (ids: string[]): Promise<string[]> => {
    const pendentes = [...ids]
    try {
      while (pendentes.length > 0) {
        const id = pendentes[0]!
        if (draftFor && draftFor !== 'orphans') {
          await addItem.mutateAsync({ checklistId: draftFor, linkedWorkItemId: id })
        } else {
          // sem checklist no contexto = vincular como filho DIRETO
          await linkChild.mutateAsync(id)
        }
        pendentes.shift()
      }
      setDraftFor(null)
      setDraftText('')
    } catch {
      // o aviso de erro já aparece
    }
    return pendentes
  }

  const submitDraft = () => {
    const text = draftText.trim()
    if (!text || draftFor === null) return
    if (draftFor === 'orphans') {
      // novo filho direto: cria WI filho via checklist? nao — cria item de trabalho filho
      createChildFromTitle.mutate(text)
    } else {
      addItem.mutate({ checklistId: draftFor, text })
    }
    // Enter salva e ja abre o proximo: quem cria checklist costuma digitar
    // varios itens seguidos. Para sair, Esc ou clicar fora.
    setDraftText('')
  }

  /**
   * Colou um texto com VÁRIAS linhas: cada linha vira um item, na ordem
   * (SPEC-20261010-1307-colar-varias-linhas-no-checklist). Um de cada vez — a ordem do checklist é a
   * ordem de chegada. O que já estava digitado no campo entra primeiro.
   */
  const [incluindoVarios, setIncluindoVarios] = useState(false)
  const incluirVarias = async (linhas: string[]) => {
    if (draftFor === null || linhas.length === 0) return
    setIncluindoVarios(true)
    try {
      for (const texto of linhas) {
        if (draftFor === 'orphans') await createChildFromTitle.mutateAsync(texto)
        else await addItem.mutateAsync({ checklistId: draftFor, text: texto })
      }
    } catch {
      // o aviso de erro já aparece; o que entrou, entrou
    } finally {
      setIncluindoVarios(false)
    }
  }

  const createChildFromTitle = useWiMutation(workItemId, async (title: string) => {
    const bootstrap = await (await api.catalog.bootstrap.$get()).json()
    const initial = bootstrap.workItemStatuses.find((s) => s.isInitial) ?? bootstrap.workItemStatuses[0]!
    return api['work-items'].$post({
      json: {
        title,
        description: '',
        workItemTypeId: detail.item.workItemTypeId,
        workItemStatusId: initial.id,
        workItemPriorityId: detail.item.workItemPriorityId,
        parentWorkItemId: workItemId,
        companyId: detail.item.companyId,
        companyAreaId: detail.item.companyAreaId,
        portfolioNodeId: detail.item.portfolioNodeId,
      } as never,
    })
  })

  /**
   * Grava o titulo editado por dois cliques.
   *
   * Item simples altera o texto do proprio checklist; item vinculado altera o
   * TITULO do Item de Trabalho — por isso o `linkedId` no estado.
   */
  const salvarTitulo = () => {
    if (!editandoTitulo) return
    const texto = editandoTitulo.texto.trim()
    const { id, linkedId } = editandoTitulo
    setEditandoTitulo(null)
    if (texto === '') return
    if (linkedId) renameLinked.mutate({ id: linkedId, title: texto })
    else renameItem.mutate({ itemId: id, text: texto })
  }

  // usado só no picker de busca (resultados vêm com status por código)
  const statusLabel = (code: string) => {
    const key = `work_item_status.${code.toLowerCase()}.name`
    const label = t(key)
    return label === key ? code : label
  }

  const renderRow = (item: ChecklistItem, checklistId: string | null) => {
    const done = item.isDone
    const abrivel = temNetos(item.linked?.id)
    const aberta = abertas.has(item.id)
    return (
      <Fragment key={item.id}>
      <div
        className="wi-cl__row"
        data-item-id={item.id}
        onDragOver={(e) => {
          const a = arrasto.current
          // de qualquer checklist: mover entre listas é o mesmo gesto de
          // reordenar, só que soltando em outra (2026-09-23)
          if (!a || a.id === item.id) return
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          const r = e.currentTarget.getBoundingClientRect()
          const antes = e.clientY < r.top + r.height / 2
          if (a.alvo === item.id && a.antes === antes) return
          a.alvo = item.id
          a.antes = antes
          marcarDestino(e.currentTarget, antes)
        }}
        onDrop={(e) => {
          e.preventDefault()
          soltarLinha(item, checklistId)
        }}
      >
        {/* alça de arrastar: só ela puxa a linha, para não atrapalhar o clique
            no texto e na caixa de concluído */}
        <span
          className="wi-cl__grip"
          draggable
          role="button"
          tabIndex={0}
          aria-label={`Mover ${item.text || item.linked?.title || 'item'}`}
          title="Arraste para mudar a ordem"
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move'
            e.dataTransfer.setData('text/plain', item.id)
            arrasto.current = { id: item.id, checklistId, alvo: null, antes: true }
            const linha = e.currentTarget.closest('.wi-cl__row') as HTMLElement | null
            if (linha) {
              e.dataTransfer.setDragImage(linha, 16, 14)
              linha.classList.add('wi-cl__row--indo')
            }
          }}
          onDragEnd={limparArrasto}
        >
          <i className="fas fa-grip-vertical" aria-hidden />
        </span>
        {abrivel ? (
          <button
            type="button"
            className="wi-cl__netotog"
            aria-expanded={aberta}
            aria-label={aberta ? 'Recolher os checklists deste item' : 'Expandir os checklists deste item'}
            title={aberta ? 'Recolher' : 'Ver os checklists deste item'}
            onClick={() => alternarAberta(item.id)}
          >
            <i className={`fas fa-chevron-${aberta ? 'down' : 'right'}`} aria-hidden />
          </button>
        ) : (
          <span className="wi-cl__netotog wi-cl__netotog--vazio" aria-hidden />
        )}
        <button
          type="button"
          className={`wi-checkbox${done ? ' wi-checkbox--done' : ''}`}
          aria-label="Marcar como concluído"
          aria-pressed={done}
          title={item.linked ? 'Concluir/reabrir o Item de Trabalho vinculado' : 'Marcar como concluído'}
          onClick={() => {
            /*
              CONCLUIR arrasta o que está embaixo (SPEC-20261006-1027-concluir-em-cadeia): um item
              "concluído" com 0/6 atividades abertas é uma contradição. A
              confirmação mostra o que vai junto ANTES de gravar qualquer coisa.

              REABRIR pergunta (SPEC-20261006-1355-desmarcar-em-cadeia): com
              coisas concluídas embaixo, a pessoa escolhe entre reabrir só o
              item ou tudo; sem nada embaixo, reabre direto. Nunca desfaz em
              cadeia sem perguntar — seria desfazer trabalho que ninguém pediu.

              Linha solta, sem item vinculado, não tem nada embaixo: continua
              valendo o clique direto.
            */
            if (item.linked && !done) setConcluindoEmCadeia({ id: item.linked.id, titulo: item.linked.title })
            else if (item.linked) void pedirReabertura({ id: item.linked.id, title: item.linked.title })
            else patchItem.mutate({ itemId: item.id, isDone: !done })
          }}
        >
          <i className="fas fa-check" aria-hidden />
        </button>

        {editandoTitulo?.id === item.id ? (
          <input
            className="wi-draft__input"
            style={{ flex: 1, minWidth: 0 }}
            autoFocus
            value={editandoTitulo.texto}
            aria-label="Título do item"
            onChange={(e) => setEditandoTitulo({ ...editandoTitulo, texto: e.target.value })}
            onBlur={() => salvarTitulo()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                salvarTitulo()
              }
              if (e.key === 'Escape') setEditandoTitulo(null)
            }}
          />
        ) : item.linked ? (
          <>
            <button
              type="button"
              className="wi-cl__wilink"
              title="Um clique abre o Item de Trabalho · dois cliques editam o texto aqui"
              // O clique simples espera: sem isso ele navegaria na hora e o
              // segundo clique nunca chegaria a acontecer.
              onClick={() => {
                if (cliqueRef.current) clearTimeout(cliqueRef.current)
                const alvo = item.linked!.id
                cliqueRef.current = setTimeout(() => {
                  cliqueRef.current = null
                  onOpenItem(alvo)
                }, 220)
              }}
              onDoubleClick={(e) => {
                e.preventDefault()
                if (cliqueRef.current) {
                  clearTimeout(cliqueRef.current)
                  cliqueRef.current = null
                }
                setEditandoTitulo({ id: item.id, texto: item.linked!.title, linkedId: item.linked!.id })
              }}
            >
              <span className="wi-cl__wicode">{item.linked.code}</span>
              <span className={`wi-cl__text${done ? ' wi-cl__text--done' : ''}`}>{item.linked.title}</span>
              <i className="fas fa-external-link-alt" aria-hidden style={{ fontSize: 11, color: 'var(--ob-primary)', flex: 'none' }} />
            </button>
            <ChildRowControls parentId={detail.item.id} child={item.linked} bootstrap={bootstrap} />
            {botaoDeData(item)}
            <button
              type="button"
              className="wi-cl__edit"
              aria-label="Adicionar checklist neste item"
              title="Adicionar checklist neste item (sem precisar abrir ele)"
              onClick={() => {
                setNameModal({ mode: 'create-filho', filhoId: item.linked!.id, rowId: item.id })
                setNameValue('')
              }}
            >
              <i className="fas fa-list-ul" aria-hidden />
            </button>
            <button
              type="button"
              className="wi-cl__edit"
              aria-label="Editar título"
              title="Editar título"
              onClick={() => setEditandoTitulo({ id: item.id, texto: item.linked!.title, linkedId: item.linked!.id })}
            >
              <i className="fas fa-pen" aria-hidden />
            </button>
            <button
              type="button"
              className="wi-cl__delete"
              aria-label="Remover"
              title="Remover: desfazer o vínculo ou excluir o Item de Trabalho"
              onClick={() =>
                // antes a lixeira só desfazia o vínculo, e não havia como
                // excluir o item pelo checklist (SPEC-20261006-1304)
                setRemovendoFilho({
                  id: item.linked!.id,
                  code: item.linked!.code,
                  title: item.linked!.title,
                  linhaId: item.id,
                })
              }
            >
              <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
            </button>
          </>
        ) : (
          <>
            <span
              className={`wi-cl__text${done ? ' wi-cl__text--done' : ''}`}
              style={{ flex: 1, minWidth: 0, cursor: 'text' }}
              title="Dois cliques para editar"
              onDoubleClick={() => setEditandoTitulo({ id: item.id, texto: item.text, linkedId: null })}
            >
              {item.text}
            </span>
            <button
              type="button"
              className="wi-cl__convert"
              title="Converter em Item de Trabalho filho"
              onClick={() => setConvertendo({ id: item.id, texto: item.text })}
            >
              <i className="fas fa-sitemap" aria-hidden style={{ fontSize: 8 }} />
              Converter em item
            </button>
            {botaoDeMembros(item)}
            {botaoDeData(item)}
            <button
              type="button"
              className="wi-cl__edit"
              aria-label="Editar descrição"
              title="Editar descrição"
              onClick={() => setEditandoTitulo({ id: item.id, texto: item.text, linkedId: null })}
            >
              <i className="fas fa-pen" aria-hidden />
            </button>
            <button
              type="button"
              className="wi-cl__delete"
              aria-label="Remover item do checklist"
              title="Remover item do checklist"
              onClick={() => confirm.ask('O item do checklist será removido.', () => deleteItem.mutate(item.id))}
            >
              <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
            </button>
          </>
        )}
      </div>
      {/* Os checklists do item desta linha, com as MESMAS ações — é o próprio
          componente que se repete, então nada diverge entre os níveis. */}
      {abrivel && aberta && item.linked ? (
        <div className="wi-cl__neto">
          <SubChecklists itemId={item.linked.id} bootstrap={bootstrap} onOpenItem={onOpenItem} />
        </div>
      ) : null}
      </Fragment>
    )
  }

  const renderDraft = (target: string | 'orphans', placeholder: string) =>
    draftFor === target ? (
      <div className="wi-draft">
        <div className="wi-draft__row">
          <input
            className="wi-draft__input"
            autoFocus
            placeholder={incluindoVarios ? 'Incluindo os itens colados…' : placeholder}
            value={draftText}
            disabled={incluindoVarios}
            onChange={(e) => setDraftText(e.target.value)}
            onPaste={(e) => {
              // várias linhas coladas = vários itens (SPEC-20261010-1307-colar-varias-linhas-no-checklist)
              const linhas = linhasColadas(e.clipboardData.getData('text'))
              if (linhas.length < 2) return
              e.preventDefault()
              const digitado = draftText.trim()
              setDraftText('')
              void incluirVarias(digitado ? [digitado, ...linhas] : linhas)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submitDraft()
              }
              if (e.key === 'Escape') {
                setDraftFor(null)
                setDraftText('')
              }
            }}
          />
          <button
            type="button"
            className="wi-draft__pick"
            aria-label="Selecionar item existente"
            title="Selecionar um Item de Trabalho existente"
            onClick={abrirPicker}
          >
            <i className="fas fa-search-plus" aria-hidden style={{ fontSize: 13 }} />
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            type="button"
            className="wi-cal__save"
            style={{ width: 'auto', height: 34, padding: '0 16px', marginBottom: 0, opacity: draftText.trim() ? 1 : 0.5 }}
            disabled={!draftText.trim()}
            onClick={submitDraft}
          >
            Salvar
          </button>
          <button
            type="button"
            className="ob-rte__cancel"
            style={{ height: 34 }}
            onClick={() => {
              setDraftFor(null)
              setDraftText('')
            }}
          >
            Cancelar
          </button>
        </div>
      </div>
    ) : null

  // "Filhos" lista TODOS os filhos, inclusive os que ja aparecem num
  // checklist: convertido continua no checklist E entra aqui como referencia.
  return (
    <div ref={caixaRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {(mostrarChecklists ? detail.collab.checklists : []).map((checklist) => {
        const done = checklist.items.filter((i) => i.isDone).length
        const total = checklist.items.length
        const ratio = total === 0 ? 0 : done / total
        return (
          <div key={checklist.id} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div className="wi-cl__header">
              {/* setinha SEMPRE visível no início; o ícone do checklist vem depois */}
              <button
                type="button"
                className="wi-cl__toggle"
                aria-expanded={!recolhidos.has(checklist.id)}
                aria-label={recolhidos.has(checklist.id) ? 'Expandir checklist' : 'Recolher checklist'}
                title={recolhidos.has(checklist.id) ? 'Expandir' : 'Recolher'}
                onClick={() =>
                  setRecolhidos((atual) => {
                    const proximo = new Set(atual)
                    if (proximo.has(checklist.id)) proximo.delete(checklist.id)
                    else proximo.add(checklist.id)
                    return proximo
                  })
                }
              >
                <i
                  className={`fas ${recolhidos.has(checklist.id) ? 'fa-chevron-right' : 'fa-chevron-down'} wi-cl__chev`}
                  aria-hidden
                />
              </button>
              <i className="fas fa-list-ul wi-cl__ico" aria-hidden />
              <button
                type="button"
                className="wi-cl__name"
                title="Clique para renomear o checklist"
                onClick={() => {
                  setNameModal({ mode: 'rename', checklist })
                  setNameValue(checklist.name)
                }}
              >
                {checklist.name}
              </button>
              <span className="wi-cl__progress">
                {done}/{total}
              </span>
              <span className="wi-cl__bar">
                <span className={`wi-cl__fill${ratio === 1 && total > 0 ? ' wi-cl__fill--done' : ''}`} style={{ width: `${ratio * 100}%` }} />
              </span>
              <button
                type="button"
                className="wi-cl__iconbtn"
                aria-label="Adicionar item ao checklist"
                title="Adicionar item ao checklist"
                onClick={() => {
                  setDraftFor(checklist.id)
                  setDraftText('')
                }}
              >
                <i className="fas fa-plus" aria-hidden style={{ fontSize: 11 }} />
              </button>
              {botaoDeDataDoChecklist(checklist)}
              <button
                type="button"
                className="wi-cl__iconbtn"
                aria-label="Renomear checklist"
                title="Renomear checklist"
                onClick={() => {
                  setNameModal({ mode: 'rename', checklist })
                  setNameValue(checklist.name)
                }}
              >
                <i className="fas fa-pen" aria-hidden style={{ fontSize: 10 }} />
              </button>
              <button
                type="button"
                className="wi-cl__iconbtn wi-cl__iconbtn--danger"
                aria-label="Excluir checklist"
                title="Excluir checklist"
                onClick={() => void pedirExclusaoDoChecklist(checklist)}
              >
                <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
              </button>
            </div>
            {recolhidos.has(checklist.id) ? null : (
              <div
                className="wi-cl__items wi-cl__zebra"
                /*
                 * Soltar no VAZIO da lista manda para o fim dela — é o único
                 * jeito de entrar num checklist sem nenhuma linha, e o gesto
                 * natural de quem quer "jogar no fim".
                 */
                onDragOver={(e) => {
                  const a = arrasto.current
                  if (!a) return
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'move'
                }}
                onDrop={(e) => {
                  // linha solta SOBRE outra já resolveu no onDrop dela
                  if (e.defaultPrevented) return
                  e.preventDefault()
                  soltarNoFim(checklist.id)
                }}
              >
                {checklist.items.map((item) => renderRow(item, checklist.id))}
                {renderDraft(checklist.id, 'Descrição do novo item (Enter salva · Esc cancela)')}
              </div>
            )}
          </div>
        )
      })}

      {mostrarFilhos && (detail.children.all.length > 0 || draftFor === 'orphans') ? (
        <div>
          {/* O tópico "Filhos" fica AQUI, logo acima da lista de filhos puros
              (sem os checklists, que ficam acima). Alinhado com os demais. */}
          <SectionHeader
            icon="fas fa-sitemap"
            title="Filhos"
            info={infoFilhos}
            count={detail.children.all.length}
            open={filhosOpen}
            onToggle={() => onToggleFilhos?.()}
            right={
              onAddChild ? (
                <button
                  type="button"
                  className="wi-cl__iconbtn"
                  aria-label="Adicionar filho"
                  title="Adicionar filho — novo ou vincular um existente"
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddChild()
                  }}
                >
                  <i className="fas fa-plus" aria-hidden style={{ fontSize: 11 }} />
                </button>
              ) : null
            }
          />
          {filhosOpen ? (
            <div className="wi-sec__body wi-cl__zebra" style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 8 }}>
          {detail.children.all.map((child) => (
            <Fragment key={child.id}>
            <div className="wi-cl__row">
              {/* Filho direto NAO tem check: este bloco so LISTA os filhos do
                  item. Concluir e coisa do proprio filho (ou de um item de
                  checklist, que existe para ser marcado). O estado aparece na
                  pill de status, mais adiante na linha. */}
              <span className="wi-cl__childmark" aria-hidden>
                <i className="fas fa-level-down-alt" />
              </span>
              {/* setinha quando o filho tem checklist: abre AQUI, sem navegar.
                  Sem checklist fica o espaco vazio: a coluna do titulo nao pode
                  dancar de uma linha para a outra. */}
              {temNetos(child.id) || abertas.has(child.id) ? (
                <button
                  type="button"
                  className="wi-cl__netotog"
                  aria-expanded={abertas.has(child.id)}
                  aria-label={abertas.has(child.id) ? 'Recolher os checklists deste filho' : 'Ver os checklists deste filho'}
                  title={abertas.has(child.id) ? 'Recolher' : 'Ver os checklists deste filho'}
                  onClick={() => alternarAberta(child.id)}
                >
                  <i className={`fas fa-chevron-${abertas.has(child.id) ? 'down' : 'right'}`} aria-hidden />
                </button>
              ) : (
                <span className="wi-cl__netotog wi-cl__netotog--vazio" aria-hidden />
              )}
              {editandoTitulo?.id === child.id ? (
                <input
                  className="wi-draft__input"
                  style={{ flex: 1, minWidth: 0 }}
                  autoFocus
                  value={editandoTitulo.texto}
                  aria-label="Título do filho"
                  onChange={(e) => setEditandoTitulo({ ...editandoTitulo, texto: e.target.value })}
                  onBlur={() => salvarTitulo()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      salvarTitulo()
                    }
                    if (e.key === 'Escape') setEditandoTitulo(null)
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="wi-cl__wilink"
                  title="Um clique abre o Item de Trabalho · dois cliques editam o título aqui"
                  // `detail` conta os cliques da sequencia: no segundo, cancela
                  // a navegacao agendada e edita aqui mesmo. Mais confiavel que
                  // depender do evento `dblclick`, que se perde se o elemento
                  // for recriado entre um clique e outro.
                  onClick={(e) => {
                    if (cliqueRef.current) {
                      clearTimeout(cliqueRef.current)
                      cliqueRef.current = null
                    }
                    if (e.detail >= 2) {
                      setEditandoTitulo({ id: child.id, texto: child.title, linkedId: child.id })
                      return
                    }
                    cliqueRef.current = setTimeout(() => {
                      cliqueRef.current = null
                      onOpenItem(child.id)
                    }, 250)
                  }}
                >
                  <span className="wi-cl__wicode">{child.code}</span>
                  <span className={`wi-cl__text${child.status.isTerminal ? ' wi-cl__text--done' : ''}`}>{child.title}</span>
                  <i className="fas fa-external-link-alt" aria-hidden style={{ fontSize: 11, color: 'var(--ob-primary)', flex: 'none' }} />
                </button>
              )}
              <ChildRowControls parentId={detail.item.id} child={child} bootstrap={bootstrap} />
              <button
                type="button"
                className="wi-cl__edit"
                aria-label="Adicionar checklist neste filho"
                title="Adicionar checklist neste filho (sem precisar abrir ele)"
                onClick={() => {
                  setNameModal({ mode: 'create-filho', filhoId: child.id, rowId: child.id })
                  setNameValue('')
                }}
              >
                <i className="fas fa-list-ul" aria-hidden />
              </button>
              <button
                type="button"
                className="wi-cl__edit"
                aria-label="Editar título"
                title="Editar título"
                onClick={() => setEditandoTitulo({ id: child.id, texto: child.title, linkedId: child.id })}
              >
                <i className="fas fa-pen" aria-hidden />
              </button>
              <button
                type="button"
                className="wi-cl__delete"
                aria-label="Remover filho"
                title="Remover: desvincular ou excluir o Item de Trabalho"
                onClick={() => setRemovendoFilho({ id: child.id, code: child.code, title: child.title })}
              >
                <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 10 }} />
              </button>
            </div>
            {/* Os checklists do filho, com as mesmas acoes de qualquer nivel —
                e dentro deles o MESMO botao de checklist, descendo sem limite. */}
            {abertas.has(child.id) ? (
              <div className="wi-cl__neto">
                <SubChecklists itemId={child.id} bootstrap={bootstrap} onOpenItem={onOpenItem} />
              </div>
            ) : null}
            </Fragment>
          ))}
          {renderDraft('orphans', 'Título do novo filho (Enter salva · Esc cancela)')}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Remover um filho tem DOIS significados: desfazer o vinculo ou apagar
          o Item de Trabalho. Perguntar qual evita destruir trabalho por um
          clique na lixeira. */}
      {removendoFilho ? (
        <>
          <div className="wi-modal__backdrop" onClick={() => setRemovendoFilho(null)} />
          <div className="wi-modal wi-modal--name" role="dialog" aria-label="Remover filho">
            <h2 className="wi-modal__title">
              Remover {removendoFilho.code} — {removendoFilho.title}
            </h2>
            <p className="wi-choose__lead">O que você quer fazer com este filho?</p>

            <button
              type="button"
              className="wi-choose"
              onClick={() => {
                if (removendoFilho.linhaId) deleteItem.mutate(removendoFilho.linhaId)
                else unlinkChild.mutate(removendoFilho.id)
                setRemovendoFilho(null)
              }}
            >
              <i className="fas fa-unlink" aria-hidden />
              <span>
                <strong>Desfazer só o vínculo</strong>
                <small>
                  {removendoFilho.linhaId
                    ? 'A linha sai do checklist; o Item de Trabalho continua existindo.'
                    : 'O Item de Trabalho continua existindo, fora deste pai.'}
                </small>
              </span>
            </button>

            <button
              type="button"
              className="wi-choose wi-choose--danger"
              onClick={() => {
                const alvo = removendoFilho
                setRemovendoFilho(null)
                void pedirExclusao(alvo)
              }}
            >
              <i className="fas fa-trash-alt" aria-hidden />
              <span>
                <strong>Excluir o Item de Trabalho</strong>
                <small>Sai das listas e dos quadros. Se tiver filhos, perguntaremos por eles.</small>
              </span>
            </button>

            <div className="wi-choose__foot">
              <button type="button" className="wi-choose__cancel" onClick={() => setRemovendoFilho(null)}>
                Cancelar
              </button>
            </div>
          </div>
        </>
      ) : null}

      {/* modal nome do checklist */}
      {nameModal ? (
        <>
          <div className="wi-modal__backdrop" onClick={() => setNameModal(null)} />
          <div className="wi-modal wi-modal--name" role="dialog" aria-label="Nome do checklist">
            <h2 className="wi-modal__title">
              {nameModal.mode === 'rename' ? 'Renomear checklist' : nameModal.mode === 'create-filho' ? 'Novo checklist no filho' : 'Novo checklist'}
            </h2>
            <div style={{ position: 'relative', width: '100%', marginBottom: 14 }}>
              <input
                className="wi-draft__input"
                style={{ width: '100%', paddingRight: 40 }}
                autoFocus
                placeholder="Nome do checklist"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') confirmarNome()
                  if (e.key === 'Escape') setNameModal(null)
                }}
              />
              <button
                type="button"
                aria-label="Confirmar"
                title="Confirmar"
                style={{
                  position: 'absolute',
                  right: 5,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 28,
                  height: 28,
                  border: 'none',
                  borderRadius: 7,
                  background: 'var(--ob-primary)',
                  color: 'var(--ob-on-primary)',
                  cursor: 'pointer',
                }}
                onClick={confirmarNome}
              >
                <i className="fas fa-check" aria-hidden style={{ fontSize: 11 }} />
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" className="ob-rte__cancel" onClick={() => setNameModal(null)}>
                Cancelar
              </button>
              <button
                type="button"
                className="ob-rte__save"
                style={{ opacity: nameValue.trim() ? 1 : 0.5 }}
                disabled={!nameValue.trim()}
                onClick={confirmarNome}
              >
                {nameModal.mode === 'rename' ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </div>
        </>
      ) : null}

      {/* selecionar item existente: o seletor único, com busca e filtro */}
      {pickerOpen ? (
        <SeletorDeItens
          titulo={draftFor === 'orphans' ? 'Vincular itens como filhos' : 'Selecionar item existente'}
          excluir={draftFor === 'orphans' ? idsInvalidosComoFilho : idsJaNoChecklist}
          rotuloConfirmar={(n) => (n > 1 ? `Incluir ${n} itens` : 'Incluir')}
          onConfirmar={incluirMarcados}
          onFechar={fecharPicker}
        />
      ) : null}

      {/* Converter linha em Item de Trabalho: pergunta tipo, status, etapa e
          descrição antes de criar (pedido 2026-08-30). */}
      {convertendo ? (
        <ConverterItemModal
          texto={convertendo.texto}
          bootstrap={bootstrap}
          convertendo={convertItem.isPending}
          onFechar={() => setConvertendo(null)}
          onConfirmar={(dados) =>
            convertItem.mutate(
              { itemId: convertendo.id, ...dados },
              { onSuccess: () => setConvertendo(null) },
            )
          }
        />
      ) : null}

      {/* Excluir com descendência: só ele, ou a árvore? (SPEC-20261006-1304) */}
      {excluindoComFilhos ? (
        <ExcluirComFilhosModal
          codigo={excluindoComFilhos.code}
          titulo={excluindoComFilhos.title}
          filhos={excluindoComFilhos.filhos}
          excluindo={archiveChild.isPending}
          onFechar={() => setExcluindoComFilhos(null)}
          onSomenteEste={() =>
            archiveChild.mutate(
              { id: excluindoComFilhos.id, comFilhos: false, linhaId: excluindoComFilhos.linhaId },
              { onSuccess: () => setExcluindoComFilhos(null) },
            )
          }
          onComFilhos={() =>
            archiveChild.mutate(
              { id: excluindoComFilhos.id, comFilhos: true, linhaId: excluindoComFilhos.linhaId },
              { onSuccess: () => setExcluindoComFilhos(null) },
            )
          }
        />
      ) : null}

      {/* Apagar checklist com itens vinculados: eles ficam ou vão? */}
      {excluindoChecklist ? (
        <ExcluirChecklistModal
          nome={excluindoChecklist.nome}
          itens={excluindoChecklist.itens}
          filhos={excluindoChecklist.filhos}
          excluindo={removeChecklist.isPending}
          onFechar={() => setExcluindoChecklist(null)}
          onManter={() =>
            removeChecklist.mutate(
              { checklistId: excluindoChecklist.id, itens: 'manter' },
              { onSuccess: () => setExcluindoChecklist(null) },
            )
          }
          onExcluir={(comFilhos) =>
            removeChecklist.mutate(
              { checklistId: excluindoChecklist.id, itens: comFilhos ? 'excluir-com-filhos' : 'excluir' },
              { onSuccess: () => setExcluindoChecklist(null) },
            )
          }
        />
      ) : null}

      {/* Conclusao em cadeia: a lista do que vai junto, antes de gravar. */}
      {concluindoEmCadeia ? (
        <ConcluirEmCadeiaModal
          itemId={concluindoEmCadeia.id}
          titulo={concluindoEmCadeia.titulo}
          onFechar={() => setConcluindoEmCadeia(null)}
        />
      ) : null}

      {/* Reabertura em cadeia: só o item, tudo, ou nada (SPEC-20261006-1355). */}
      {reabrindoEmCadeia ? (
        <ReabrirEmCadeiaModal
          itemId={reabrindoEmCadeia.id}
          titulo={reabrindoEmCadeia.titulo}
          nos={reabrindoEmCadeia.nos}
          onSoEste={() => toggleLinkedStatus.mutate({ linkedId: reabrindoEmCadeia.id, done: false })}
          onFechar={() => setReabrindoEmCadeia(null)}
        />
      ) : null}

      {confirm.element}
    </div>
  )
}

export type ChecklistsApi = {
  openCreateModal: () => void
}
